import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight } from "lucide-react";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 10;

/** Redirected to from Stripe Checkout (`?session_id=…`).
 *  Polls /api/payments/checkout/status/{session_id} until paid / expired / max.
 */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const nav = useNavigate();
  const { refresh } = useAuth();

  const [state, setState] = useState("polling");   // polling | paid | failed | expired | timeout
  const [info,  setInfo]  = useState({});
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); setInfo({ msg: "Missing checkout session id." }); return; }
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (cancelled) return;
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          await refresh();
          return;
        }
        if (data.status === "expired") { setState("expired"); return; }
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) { setState("timeout"); return; }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        setState("failed");
        setInfo({ msg: formatApiErrorDetail(err.response?.data?.detail) || "Could not verify payment" });
      }
    };
    // Give the user 800ms of spinner so the success-path UX feels stable
    // even when the first poll resolves instantly (paid via webhook).
    const initial = setTimeout(poll, 800);
    return () => { cancelled = true; clearTimeout(initial); };
  }, [sessionId, refresh]);

  return (
    <div className="mx-auto max-w-xl px-4 sm:px-6 lg:px-8 py-16" data-testid="payment-success-page">
      <div className="cs-card rounded-2xl p-10 text-center">
        {state === "polling" && (
          <>
            <Loader2 size={32} className="mx-auto animate-spin text-white/70 mb-4" data-testid="payment-spinner" />
            <h1 className="font-display text-2xl uppercase tracking-tight">Verifying Payment…</h1>
            <p className="mt-3 text-sm text-white/60">Hang on while we confirm with Stripe.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={36} className="mx-auto text-emerald-400 mb-4" data-testid="payment-paid" />
            <h1 className="font-display text-2xl uppercase tracking-tight">Plan Upgraded</h1>
            <p className="mt-3 text-sm text-white/70">
              You&apos;re now on{" "}
              <span className="font-semibold text-white uppercase tracking-[0.18em]">
                {info.target_plan || "the new plan"}
              </span>. Your crew cap has been lifted instantly.
            </p>
            <button
              onClick={() => nav("/dashboard")}
              className="btn-themed h-11 mt-6 px-6 rounded-full text-xs uppercase tracking-[0.25em] inline-flex items-center"
              data-testid="payment-continue"
            >
              Go to Dashboard <ArrowRight size={14} className="ml-2" />
            </button>
          </>
        )}
        {(state === "expired" || state === "timeout" || state === "failed") && (
          <>
            <AlertTriangle size={32} className="mx-auto text-amber-400 mb-4" />
            <h1 className="font-display text-2xl uppercase tracking-tight">
              {state === "expired" ? "Session Expired" :
                state === "timeout" ? "Still Pending" : "Couldn't Verify"}
            </h1>
            <p className="mt-3 text-sm text-white/65">
              {info.msg ||
                "We didn't receive a confirmation in time. If your card was charged, the plan will update automatically once Stripe finishes the webhook handshake."}
            </p>
            <Link
              to="/pricing"
              className="inline-block mt-6 text-xs uppercase tracking-[0.25em] text-[#F5B82E] hover:underline"
            >
              Back to Pricing
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
